import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Progress } from '../../components/ui/progress';
import { Badge } from '../../components/ui/badge';
import { Target, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function MyGoalsPage() {
  const [goals, setGoals] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [g, r] = await Promise.all([
          axios.get(`${API}/api/employee-portal/goals`),
          axios.get(`${API}/api/employee-portal/performance/reviews`).catch(() => ({ data: [] })),
        ]);
        setGoals(g.data.goals || g.data || []);
        setReviews(r.data.reviews || r.data || []);
      } catch(e){ if(e?.response?.status !== 403) toast.error('Failed to load goals'); }
      finally { setLoading(false); }
    })();
  }, []);

  const avg = goals.length ? Math.round(goals.reduce((a, g) => a + (g.progress || 0), 0) / goals.length) : 0;

  return (
    <div className="min-h-screen p-6 bg-gray-50" data-testid="employee-goals-page">
      <div className="max-w-6xl mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Goals & KPIs</h1>
          <p className="text-gray-600 mt-1">Your 30-60-90 day objectives and performance reviews.</p>
        </div>

        <Card><CardContent className="p-6 flex items-center justify-between">
          <div>
            <div className="text-sm uppercase tracking-wider text-gray-500 mb-1">Overall progress</div>
            <div className="text-4xl font-bold text-emerald-600" data-testid="goals-overall">{avg}%</div>
            <div className="text-xs text-gray-500 mt-1">across {goals.length} active goal{goals.length !== 1 ? 's' : ''}</div>
          </div>
          <TrendingUp className="h-12 w-12 text-emerald-500 opacity-40" />
        </CardContent></Card>

        {loading ? <div className="text-gray-500">Loading…</div> :
         goals.length === 0 ? <Card><CardContent className="p-8 text-center text-gray-500 italic">No goals assigned. Talk to your manager to set some.</CardContent></Card> :
         <div className="space-y-3">
           {goals.map((g, i) => (
             <Card key={i} data-testid={`goal-card-${i}`}>
               <CardContent className="p-5">
                 <div className="flex items-start justify-between gap-4 mb-3">
                   <div className="flex items-center gap-3 flex-1">
                     <div className="bg-orange-100 p-2 rounded-lg"><Target className="h-5 w-5 text-orange-600" /></div>
                     <div className="flex-1">
                       <div className="font-semibold text-gray-900">{g.title || g.name}</div>
                       {g.description && <div className="text-sm text-gray-600 mt-0.5">{g.description}</div>}
                     </div>
                   </div>
                   <Badge variant="outline">{g.period || g.horizon || '30d'}</Badge>
                 </div>
                 <Progress value={g.progress || 0} className="h-2" />
                 <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
                   <span>{g.progress || 0}% complete</span>
                   {g.target && <span>Target: {g.target}</span>}
                   {g.due_date && <span>Due {g.due_date}</span>}
                 </div>
               </CardContent>
             </Card>
           ))}
         </div>}

        {reviews.length > 0 && <Card>
          <CardHeader><CardTitle>Recent performance reviews</CardTitle></CardHeader>
          <CardContent><div className="divide-y divide-gray-100">
            {reviews.map((r, i) => (
              <div key={i} className="py-3 flex items-center justify-between text-sm">
                <div>
                  <div className="font-medium">{r.review_period || r.period}</div>
                  <div className="text-xs text-gray-500">Reviewed by {r.reviewer_name || 'manager'}{r.review_date ? ` on ${r.review_date}` : ''}</div>
                </div>
                {r.rating && <Badge className="bg-emerald-100 text-emerald-800">{r.rating}</Badge>}
              </div>
            ))}
          </div></CardContent>
        </Card>}
      </div>
    </div>
  );
}
